import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const API_URL =
  "https://etoile-bricolage-api.imanehibouzid2001.workers.dev";

function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [currentImage, setCurrentImage] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [preview, setPreview] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ==================================================
  // CHARGEMENT DES DONNEES
  // ==================================================

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError("");

        // ----------------------------------------------
        // Charger les catégories
        // ----------------------------------------------

        const categoriesResponse = await fetch(
          `${API_URL}/api/categories`
        );

        const categoriesData = await categoriesResponse.json();

        if (!categoriesResponse.ok || !categoriesData.success) {
          throw new Error(
            categoriesData.error ||
              "Impossible de charger les catégories."
          );
        }

        setCategories(categoriesData.categories || []);

        // ----------------------------------------------
        // Charger le produit
        // ----------------------------------------------

        const productResponse = await fetch(
          `${API_URL}/api/products/${id}`
        );

        const productData = await productResponse.json();

        if (!productResponse.ok || !productData.success) {
          throw new Error(
            productData.error ||
              "Impossible de charger le produit."
          );
        }

        const product = productData.product;

        setCategoryId(String(product.category_id));
        setName(product.name || "");
        setDescription(product.description || "");
        setCurrentImage(product.image_url || "");
      } catch (err) {
        console.error(err);

        setError(
          err.message ||
            "Une erreur est survenue lors du chargement."
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // ==================================================
  // CHANGEMENT DE PHOTO
  // ==================================================

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];

    setError("");
    setSuccess("");

    if (!file) {
      setNewImage(null);
      setPreview("");
      return;
    }

    // Vérifier le type
    if (!file.type.startsWith("image/")) {
      setError("Veuillez sélectionner une image valide.");
      event.target.value = "";
      return;
    }

    // Maximum 5 Mo
    if (file.size > 5 * 1024 * 1024) {
      setError("L'image ne doit pas dépasser 5 Mo.");
      event.target.value = "";
      return;
    }

    setNewImage(file);

    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
  };

  // Nettoyer l'URL temporaire
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  // ==================================================
  // ENREGISTRER LES MODIFICATIONS
  // ==================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (!categoryId) {
      setError("Veuillez sélectionner une catégorie.");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();

      formData.append("category_id", categoryId);
      formData.append("name", name.trim());
      formData.append("description", description.trim());

      // La nouvelle photo est facultative.
      // Si aucune nouvelle photo n'est choisie,
      // l'API conserve automatiquement l'ancienne.
      if (newImage) {
        formData.append("image", newImage);
      }

      const response = await fetch(
        `${API_URL}/api/products/${id}`,
        {
          method: "PUT",
          credentials: "include",
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
            "Impossible de modifier le produit."
        );
      }

      setSuccess("✓ Produit modifié avec succès !");

      // Retour à la liste après un court délai
      setTimeout(() => {
        navigate("/admin/products");
      }, 1000);
    } catch (err) {
      console.error(err);

      setError(
        err.message ||
          "Une erreur est survenue lors de la modification."
      );
    } finally {
      setSaving(false);
    }
  };

  // ==================================================
  // CHARGEMENT
  // ==================================================

  if (loading) {
    return (
      <main style={styles.page}>
        <div style={styles.card}>
          <div style={styles.loadingBox}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>
              Chargement du produit...
            </p>
          </div>
        </div>
      </main>
    );
  }

  // ==================================================
  // AFFICHAGE
  // ==================================================

  return (
    <main style={styles.page}>
      <div style={styles.card}>
        {/* EN-TETE */}

        <div style={styles.header}>
          <p style={styles.brand}>ÉTOILE BRICOLAGE</p>

          <h1 style={styles.title}>
            Modifier le produit
          </h1>

          <p style={styles.subtitle}>
            Modifiez les informations du produit.
          </p>
        </div>

        {/* ERREUR */}

        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}

        {/* SUCCES */}

        {success && (
          <div style={styles.successMessage}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* CATEGORIE */}

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Catégorie{" "}
              <span style={styles.required}>*</span>
            </label>

            <select
              value={categoryId}
              onChange={(e) =>
                setCategoryId(e.target.value)
              }
              style={styles.input}
              disabled={saving}
              required
            >
              <option value="">
                Sélectionner une catégorie
              </option>

              {categories.map((category) => (
                <option
                  key={category.id}
                  value={category.id}
                >
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* NOM */}

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Nom du produit{" "}
              <span style={styles.optional}>
                — facultatif
              </span>
            </label>

            <input
              type="text"
              value={name}
              onChange={(e) =>
                setName(e.target.value)
              }
              placeholder="Ex. Marteau"
              style={styles.input}
              disabled={saving}
            />
          </div>

          {/* DESCRIPTION */}

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Description{" "}
              <span style={styles.optional}>
                — facultative
              </span>
            </label>

            <textarea
              value={description}
              onChange={(e) =>
                setDescription(e.target.value)
              }
              placeholder="Description du produit..."
              rows="5"
              style={{
                ...styles.input,
                ...styles.textarea,
              }}
              disabled={saving}
            />
          </div>

          {/* PHOTO ACTUELLE */}

          {currentImage && (
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Photo actuelle
              </label>

              <div style={styles.currentImageBox}>
                <img
                  src={currentImage}
                  alt={name || "Produit"}
                  style={styles.currentImage}
                />
              </div>
            </div>
          )}

          {/* NOUVELLE PHOTO */}

          <div style={styles.formGroup}>
            <label style={styles.label}>
              Remplacer la photo{" "}
              <span style={styles.optional}>
                — facultatif
              </span>
            </label>

            <div style={styles.fileBox}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                disabled={saving}
                style={styles.fileInput}
              />

              <p style={styles.fileHelp}>
                JPG, PNG, WEBP — maximum 5 Mo
              </p>

              <p style={styles.fileHelp}>
                Laissez vide pour conserver la photo
                actuelle.
              </p>
            </div>
          </div>

          {/* APERCU NOUVELLE PHOTO */}

          {preview && (
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Aperçu de la nouvelle photo
              </label>

              <div style={styles.previewBox}>
                <img
                  src={preview}
                  alt="Aperçu"
                  style={styles.previewImage}
                />
              </div>
            </div>
          )}

          {/* BOUTONS */}

          <div style={styles.buttons}>
            <button
              type="submit"
              disabled={saving}
              style={{
                ...styles.saveButton,
                ...(saving
                  ? styles.disabledButton
                  : {}),
              }}
            >
              {saving
                ? "Enregistrement..."
                : "Enregistrer les modifications"}
            </button>

            <button
              type="button"
              onClick={() =>
                navigate("/admin/products")
              }
              disabled={saving}
              style={styles.cancelButton}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

// ==================================================
// STYLES
// ==================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f5f6f2",
    padding: "40px 20px 70px",
    boxSizing: "border-box",
  },

  card: {
    width: "100%",
    maxWidth: "760px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: "16px",
    padding: "38px",
    boxSizing: "border-box",
    boxShadow: "0 10px 35px rgba(0, 0, 0, 0.07)",
  },

  header: {
    marginBottom: "28px",
  },

  brand: {
    margin: "0 0 8px",
    fontSize: "14px",
    fontWeight: "700",
    letterSpacing: "2px",
    color: "#384525",
  },

  title: {
    margin: "0",
    color: "#384525",
    fontSize: "30px",
    lineHeight: "1.2",
    fontWeight: "700",
  },

  subtitle: {
    margin: "8px 0 0",
    color: "#777",
    fontSize: "15px",
  },

  formGroup: {
    marginBottom: "22px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    color: "#222",
    fontSize: "14px",
    fontWeight: "600",
  },

  required: {
    color: "#c62828",
  },

  optional: {
    color: "#999",
    fontWeight: "400",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #dddddd",
    borderRadius: "8px",
    padding: "13px 15px",
    fontSize: "15px",
    fontFamily: "inherit",
    background: "#ffffff",
    outline: "none",
  },

  textarea: {
    resize: "vertical",
    minHeight: "120px",
  },

  currentImageBox: {
    width: "190px",
    height: "190px",
    border: "1px solid #eeeeee",
    borderRadius: "10px",
    padding: "10px",
    boxSizing: "border-box",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  currentImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },

  fileBox: {
    border: "1px dashed #cfd4c8",
    borderRadius: "8px",
    padding: "18px",
    background: "#fafbf8",
  },

  fileInput: {
    width: "100%",
    fontSize: "14px",
  },

  fileHelp: {
    margin: "8px 0 0",
    color: "#888",
    fontSize: "12px",
  },

  previewBox: {
    width: "190px",
    height: "190px",
    padding: "10px",
    borderRadius: "10px",
    background: "#f7f7f7",
    border: "1px solid #eeeeee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
  },

  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },

  buttons: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "8px",
  },

  saveButton: {
    width: "100%",
    border: "none",
    borderRadius: "8px",
    padding: "14px 20px",
    background: "#384525",
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
  },

  cancelButton: {
    width: "100%",
    border: "1px solid #384525",
    borderRadius: "8px",
    padding: "13px 20px",
    background: "#ffffff",
    color: "#384525",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
  },

  disabledButton: {
    opacity: "0.65",
    cursor: "not-allowed",
  },

  errorMessage: {
    marginBottom: "22px",
    padding: "13px 15px",
    borderRadius: "8px",
    background: "#fff1f1",
    border: "1px solid #f1c4c4",
    color: "#b42318",
    fontSize: "14px",
  },

  successMessage: {
    marginBottom: "22px",
    padding: "13px 15px",
    borderRadius: "8px",
    background: "#edf7ed",
    border: "1px solid #cce7cc",
    color: "#27632a",
    fontSize: "14px",
  },

  loadingBox: {
    minHeight: "250px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  spinner: {
    width: "35px",
    height: "35px",
    border: "4px solid #eeeeee",
    borderTop: "4px solid #384525",
    borderRadius: "50%",
  },

  loadingText: {
    marginTop: "15px",
    color: "#666",
    fontSize: "14px",
  },
};

export default EditProduct;